import { motion, AnimatePresence } from 'framer-motion';
import React, {createContext, useContext} from 'react';
import {AccordionItem, ProjectIcon, SectionCard, SectionHeader, StatusBadge} from './projectUi';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

const ProjectReadOnlyContext = createContext<boolean>(false);

export const useProjectReadOnly = () => useContext(ProjectReadOnlyContext);
export const ProjectReadOnlyProvider = ProjectReadOnlyContext.Provider;
const ProjectPublicViewContext = createContext<boolean>(false);
export const useProjectPublicView = () => useContext(ProjectPublicViewContext);
export const ProjectPublicViewProvider = ProjectPublicViewContext.Provider;

export function hasPersistedId(value: unknown): boolean {
    return typeof value === 'number' && value > 0;
}

export function buildCardTitle(
    t: TFunction<"translation", undefined>,
    fallbackLabel: string,
    index: number,
    ...parts: Array<unknown>
): string {
    const preferred = parts
        .flatMap((part) => (Array.isArray(part) ? part : [part]))
        .map((part) => normalizeTextPart(t, part))
        .find(Boolean);

    return preferred ? truncateText(preferred) : `${fallbackLabel} ${index + 1}`;
}

export function buildSummary(t: TFunction<"translation", undefined>, ...parts: Array<unknown>): string {
    const values = parts
        .flatMap((part) => (Array.isArray(part) ? part : [part]))
        .map((part) => normalizeTextPart(t, part))
        .filter(Boolean);

    return values.join(' / ');
}

export function formatSummaryNumber(value: unknown): string {
    if (typeof value !== 'number' || Number.isNaN(value) || value === 0) return '';
    return String(value);
}

function normalizeTextPart(t: TFunction<"translation", undefined>, part: unknown): string {
    if (part == null) return '';

    if (typeof part === 'number') {
        return part ? String(part) : '';
    }

    if (typeof part === 'boolean') {
        return part ? t('projectFormHelpers.yes') : t('projectFormHelpers.no');
    }

    return String(part).trim();
}

function truncateText(value: string, maxLength = 72): string {
    if (value.length <= maxLength) return value;
    return `${value.slice(0, maxLength - 3).trim()}...`;
}

export function FormSection(props: {
    id?: string;
    title: string;
    description?: string;
    children: React.ReactNode;
    collapsible?: boolean;
    defaultOpen?: boolean;
    headerActions?: React.ReactNode; // Added this prop
}) {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = React.useState<boolean>(props.defaultOpen ?? true);

    const toggle = () => {
        if (props.collapsible) {
            setIsOpen((prev) => !prev);
        }
    };

    return (
        <SectionCard id={props.id}>
            <div
                className={props.collapsible ? 'projectSectionHeaderClickable' : ''}
                onClick={toggle}
                style={props.collapsible ? {cursor: 'pointer'} : undefined}
            >
                <SectionHeader
                    icon={<ProjectIcon name={props.title}/>}
                    title={props.title}
                    description={props.description}
                    actions={
                        <>
                            {props.headerActions} {/* Render headerActions here */}
                            {props.collapsible ? (
                                <button
                                    type="button"
                                    className={`projectAccordionCaret${isOpen ? ' isOpen' : ''}`}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggle();
                                    }}
                                    title={isOpen ? t('projectFormHelpers.collapse') : t('projectFormHelpers.expand')}
                                >
                                    <svg viewBox="0 0 20 20" fill="none">
                                        <path
                                            d="M6 8l4 4 4-4"
                                            stroke="currentColor"
                                            strokeWidth="1.8"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </button>
                            ) : null}
                        </>
                    }
                />
            </div>

            {/* @ts-expect-error - React 19 typing issue with AnimatePresence */}
            <AnimatePresence initial={false}>
                {(!props.collapsible || isOpen) ? (
                     <motion.div
                     key="content"
                     initial="collapsed"
                     animate="open"
                     exit="collapsed"
                     variants={{
                         open: { opacity: 1, height: 'auto' },
                         collapsed: { opacity: 0, height: 0 },
                     }}
                     transition={{ duration: 0.3, ease: [0.04, 0.62, 0.23, 0.98] }}
                 >
                     <div className="projectSectionBody">{props.children}</div>
                 </motion.div>
                ) : null}
            </AnimatePresence>
        </SectionCard>
    );
}

export function ArraySection(props: {
    id?: string;
    title: string;
    description?: string;
    children: React.ReactNode;
}) {
    return (
        <div id={props.id} className="projectArraySection">
            <div className="projectArraySectionHeader">
        <span className="projectArraySectionIcon">
          <ProjectIcon name={props.title}/>
        </span>

                <div className="projectArraySectionCopy">
                    <h3>{props.title}</h3>
                    {props.description ? <p>{props.description}</p> : null}
                </div>
            </div>

            <div className="projectArraySectionBody">{props.children}</div>
        </div>
    );
}

export function NestedPanel(props: {
    title: string;
    children: React.ReactNode;
    actions?: React.ReactNode;
}) {
    return (
        <div className="projectNestedPanel">
            <AccordionItem title={props.title} defaultOpen={false} compact actions={props.actions}>
                {props.children}
            </AccordionItem>
        </div>
    );
}

export function ItemCard(props: {
    title: string;
    summary?: string;
    badge?: React.ReactNode;
    children: React.ReactNode;
    onRemove?: () => void;
    compact?: boolean;
    defaultOpen?: boolean;
    actions?: React.ReactNode;
}) {
    const { t } = useTranslation();
    const readOnly = useProjectReadOnly();

    return (
        <AccordionItem
            title={props.title}
            summary={props.summary}
            badge={props.badge}
            defaultOpen={props.defaultOpen ?? false}
            compact={props.compact}
            actions={
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {props.actions}
                    {props.onRemove && !readOnly ? (
                        <button
                            className="btn btnDanger btnSmall"
                            type="button"
                            onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                props.onRemove?.();
                            }}
                        >
                            {t('projectFormHelpers.delete')}
                        </button>
                    ) : null}
                </div>
            }
        >
            {props.children}
        </AccordionItem>
    );
}

export type StatusTone = 'neutral' | 'info' | 'success' | 'warning';

export function StatusPill(props: {
    label: string;
    tone?: StatusTone;
}) {
    return <StatusBadge label={props.label} tone={props.tone}/>;
}

export function EmptyState(props: { message?: string }) {
    const { t } = useTranslation();
    return (
        <p className="projectEmptyState">
            {props.message ?? t('projectFormHelpers.noInfo')}
        </p>
    );
}

export function getStatusTone(value: unknown): StatusTone {
    const normalized = String(value ?? '').trim().toLowerCase();

    if (!normalized) return 'neutral';

    if (
        normalized.includes('presentado') ||
        normalized.includes('aprobado') ||
        normalized.includes('pagad') ||
        normalized.includes('complet') ||
        normalized.includes('finaliz')
    ) {
        return 'success';
    }

    if (
        normalized.includes('pendiente') ||
        normalized.includes('revision') ||
        normalized.includes('tramite') ||
        normalized.includes('proceso')
    ) {
        return 'warning';
    }

    if (
        normalized.includes('activo') ||
        normalized.includes('abierto') ||
        normalized.includes('curso')
    ) {
        return 'info';
    }

    return 'neutral';
}

export function BulkToggle(props: {
    label: string;
    fields: any[];
    onToggle: (isPublic: boolean) => void;
}) {
    const { t } = useTranslation();
    const readOnly = useProjectReadOnly();
    
    // Determine the current state based on the fields
    const allPublic = props.fields.length > 0 && props.fields.every(f => f.publico);
    const somePublic = props.fields.length > 0 && props.fields.some(f => f.publico) && !allPublic;

    let toggleState = false; // default red
    if (allPublic) toggleState = true; // green
    
    const handleClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (readOnly) return;
        
        if (somePublic || !allPublic) {
            props.onToggle(true); // Make all public
        } else {
            props.onToggle(false); // Make all private
        }
    };

    const title = t('projectFormHelpers.bulkToggleTitle');
    
    let displayLabel = t('projectFormHelpers.private');
    if (allPublic) {
        displayLabel = t('projectFormHelpers.public');
    } else if (somePublic) {
        displayLabel = t('projectFormHelpers.somePublic');
    }

    return (
        <label
            style={{ display: 'flex', alignItems: 'center', cursor: readOnly ? 'not-allowed' : 'pointer', gap: '8px' }}
            onClick={handleClick}
            title={title}
        >
            <span style={{ fontWeight: 500 }}>{displayLabel}</span>
            <div
                style={{
                    width: '40px',
                    height: '20px',
                    backgroundColor: somePublic ? '#eab308' : (toggleState ? '#22c55e' : '#ef4444'), // Yellow for mixed, Green for all public, Red for all private
                    borderRadius: '10px',
                    position: 'relative',
                    transition: 'background-color 0.2s',
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        background: 'white',
                        top: '2px',
                        left: toggleState || somePublic ? '22px' : '2px', // Move right if all or some are public
                        transition: 'left 0.2s',
                    }}
                />
            </div>
        </label>
    );
}
